/**
 * Referee Profile Contracts
 *
 * DATABASE-FIRST: Matches get_referee_profile() stored procedure
 * Source: MCP query on 2026-01-02
 *
 * Function signature:
 *   get_referee_profile(p_identifier varchar)
 *   RETURNS TABLE(
 *     identifier varchar,
 *     referral_code varchar,
 *     name varchar,
 *     commission_percent numeric,
 *     total_earned numeric,
 *     total_lost numeric,
 *     total_withdrawn numeric,
 *     total_converted numeric,
 *     net numeric,
 *     balance numeric,
 *     created_at timestamptz
 *   )
 *
 * Table: referees (13 columns)
 *   - net: GENERATED ALWAYS AS (total_earned - total_lost)
 *   - balance: GENERATED ALWAYS AS (total_earned - total_lost - total_withdrawn - total_converted)
 */
import { z } from 'zod';
import { timestampStringRequired, successResponseSchema } from '../common.js';
// =============================================================================
// REFEREE PROFILE (from get_referee_profile function)
// =============================================================================
/**
 * Referee profile schema
 * Matches get_referee_profile() return columns exactly
 *
 * NOTE: All numeric fields are returned as numbers (not strings)
 * because the backend parses them with parseFloat() before sending
 */
export const refereeProfileSchema = z.object({
    identifier: z.string(),
    referralCode: z.string().min(3).max(20).regex(/^[A-Za-z0-9_-]+$/),
    name: z.string().nullable(),
    commissionPercent: z.number().min(0).max(100),
    totalEarned: z.number(),
    totalLost: z.number(),
    totalWithdrawn: z.number(),
    totalConverted: z.number(),
    // Generated columns (read-only, computed by database)
    net: z.number(), // total_earned - total_lost
    balance: z.number(), // total_earned - total_lost - total_withdrawn - total_converted
    createdAt: timestampStringRequired,
});
// =============================================================================
// API RESPONSE
// =============================================================================
/**
 * GET /api/referee/profile response data (inner data, for frontend consumption)
 * This is what frontend receives after ApiResponse unwrapping
 */
export const refereeProfileResponseDataSchema = z.object({
    referee: refereeProfileSchema,
});
/**
 * GET /api/referee/profile full response (with success wrapper, for validation)
 */
export const refereeProfileResponseSchema = successResponseSchema(refereeProfileResponseDataSchema);
//# sourceMappingURL=profile.js.map